import { useState, useCallback, useRef } from 'react';
import { CanvasElement, CanvasState, CANVAS_DISPLAY_HEIGHT, computeCoverDimensions } from '../types';

const MAX_HISTORY = 30;

function buildInitialState(
  bookSize: string,
  paperType: string,
  bindingType: string,
  pageCount: number,
): CanvasState {
  const dim = computeCoverDimensions(bookSize, paperType, bindingType, pageCount);
  return {
    elements: [],
    selectedId: null,
    background: '#ffffff',
    stageWidth: Math.round(dim.pageW * dim.scale),
    stageHeight: CANVAS_DISPLAY_HEIGHT,
    guides: dim.guides,
  };
}

export function useCanvasState(
  bookSize: string,
  paperType: string,
  bindingType: string,
  pageCount: number,
) {
  const [state, setState] = useState<CanvasState>(() =>
    buildInitialState(bookSize, paperType, bindingType, pageCount),
  );
  const pastRef = useRef<CanvasState[]>([]);
  const futureRef = useRef<CanvasState[]>([]);

  const commit = useCallback((next: CanvasState) => {
    pastRef.current = [...pastRef.current.slice(-MAX_HISTORY + 1), state];
    futureRef.current = [];
    setState(next);
  }, [state]);

  const addElement = useCallback((el: CanvasElement) => {
    commit({ ...state, elements: [...state.elements, el], selectedId: el.id });
  }, [commit, state]);

  const updateElement = useCallback((id: string, patch: Partial<CanvasElement>) => {
    const elements = state.elements.map(el =>
      el.id === id ? { ...el, ...patch } as CanvasElement : el,
    );
    commit({ ...state, elements });
  }, [commit, state]);

  const deleteElement = useCallback((id: string) => {
    const elements = state.elements.filter(el => el.id !== id);
    commit({ ...state, elements, selectedId: state.selectedId === id ? null : state.selectedId });
  }, [commit, state]);

  const reorderElements = useCallback((fromIndex: number, toIndex: number) => {
    const elements = [...state.elements];
    const [moved] = elements.splice(fromIndex, 1);
    elements.splice(toIndex, 0, moved);
    commit({ ...state, elements });
  }, [commit, state]);

  const setSelectedId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, selectedId: id }));
  }, []);

  const setBackground = useCallback((color: string) => {
    commit({ ...state, background: color });
  }, [commit, state]);

  // Live updates — update state for real-time preview without touching history.
  // Call the corresponding non-live version on pointer-up/blur to commit.
  const updateElementLive = useCallback((id: string, patch: Partial<CanvasElement>) => {
    setState(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === id ? { ...el, ...patch } as CanvasElement : el,
      ),
    }));
  }, []);

  const setBackgroundLive = useCallback((color: string) => {
    setState(prev => ({ ...prev, background: color }));
  }, []);

  const loadState = useCallback((newState: CanvasState) => {
    pastRef.current = [];
    futureRef.current = [];
    setState(newState);
  }, []);

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return;
    const prev = pastRef.current[pastRef.current.length - 1];
    futureRef.current = [state, ...futureRef.current];
    pastRef.current = pastRef.current.slice(0, -1);
    setState(prev);
  }, [state]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current[0];
    pastRef.current = [...pastRef.current, state];
    futureRef.current = futureRef.current.slice(1);
    setState(next);
  }, [state]);

  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;

  return {
    state,
    addElement,
    updateElement,
    updateElementLive,
    deleteElement,
    reorderElements,
    setSelectedId,
    setBackground,
    setBackgroundLive,
    loadState,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}
